import { ApolloLink, ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { onErrorLink } from '../links/onErrorLink';
import { lazyCreateManagedQueryLink } from 'owa-managed-query-link';
import { cachePolicyLoaderLink } from '../links/cachePolicyLoaderLink';
import { QueueOperationLink } from '../links/QueueOperationLink';
import { lazyCachePolicies } from 'outlook-cache-policies';
import { ResolverImports, createGraphServerLink } from './createGraphServerLink';

/**
 * These links are used on the main renderer.  The terminal link is the workerLink, which may push the request to the data worker, or,
 * may execute the graph on the renderer (based on flighting, config, etc).
 */
export async function createMainLinks(
    createWorkerLink: (inner: ApolloLink) => ApolloLink,
    context: Record<string, any>,
    resolverImports: ResolverImports,
    getClient: () => ApolloClient<NormalizedCacheObject>
): Promise<Array<ApolloLink>> {
    const links: Array<ApolloLink> = [];

    // Error link
    links.push(onErrorLink);

    // Managed query link
    if (isHostAppFeatureEnabled('managedQueryLink')) {
        const createManagedQueryLink = await lazyCreateManagedQueryLink.import();
        links.push(createManagedQueryLink());
    }

    // Queue operation link
    links.push(new QueueOperationLink());

    // Policy loader link
    const policies = await lazyCachePolicies.import();
    links.push(cachePolicyLoaderLink(policies, getClient));

    // where we execute the graph on the worker or the main render.  if the worker flight isn't
    // enabled it will forward to the localRemoteLink
    const localRemoteLink = await createGraphServerLink(context, resolverImports);
    const dataWorkerLink = createWorkerLink(localRemoteLink);
    links.push(dataWorkerLink);

    return links;
}
